import { Component } from 'react';
import { v4 } from 'uuid';
import { ChartComponentProps, ChartMetaFormField } from 'models/ui';
import { chartHelper } from 'helpers';
import { withChartLogic } from 'components/common';
import { PRODUCT_REQUEST_COMPONENTS, PRODUCT_REQUEST_FIELD_CONFIGS, ProductRequestComponentConfig, cloneFormFieldMap } from './constants';
import ProductRequestSidebarView from './ProductRequestSidebarView';

export interface ProductRequestRow {
  id: string;
  active: boolean;
  componentDesired: string;
  numberOfUnits: string;
  day: string;
  time: string;
}

export interface ProductRequestSidebarProps extends ChartComponentProps {
  isSidebarOpen: boolean;
  hasSavedRows: boolean;
  resetSignal: number;
  addProductRequests: (rows: ProductRequestRow[]) => void;
  handleCloseSidebar: Function;
}

const getFieldValue = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: string): string => {
  const formField = formFieldMap?.get(fieldName);
  if (!formField) return '';

  const selectedContent = formField.chartContent?.find((item) => !!item.value && item.selected);
  return String(selectedContent?.label ?? formField.value ?? '').trim();
};

// A unit at the end of a component label trails the typed value, so "Red Blood Cells Split Unit (mL)" plus "12" reads as "Red Blood Cells Split Unit 12 mL".
const buildComponentDesired = (label: string, detail: string): string => {
  if (!detail) return label;

  const labelWithUnit = /^(.+?)\s*\(([^()]+)\)$/.exec(label);
  return labelWithUnit ? `${labelWithUnit[1]} ${detail} ${labelWithUnit[2]}` : `${label} ${detail}`;
};

export class ProductRequestSidebar extends Component<ProductRequestSidebarProps> {
  static displayName = 'ProductRequestSidebar';

  // The values captured at the last Save. The step keeps only derived rows, which cannot be mapped
  // back onto fields, so this snapshot is what an abandoned edit reverts to.
  lastSavedFormFields: Map<string, ChartMetaFormField> | null = null;

  componentDidMount() {
    this.props.initState(this.buildFormFields());
  }

  componentDidUpdate(prevProps: ProductRequestSidebarProps) {
    if (prevProps.resetSignal !== this.props.resetSignal) {
      // A step reset clears the saved rows too, so the old snapshot must not survive it.
      this.lastSavedFormFields = null;
      this.props.initState(this.buildFormFields());
    }
  }

  // Closing is the only exit that is not a Save, so it always drops unsaved edits: back to the last
  // saved values, or to blank fields when nothing has been saved yet.
  handleCloseClick = (...args: unknown[]) => {
    this.props.initState(this.lastSavedFormFields ? cloneFormFieldMap(this.lastSavedFormFields) : this.buildFormFields());
    this.props.handleCloseSidebar(...args);
  };

  buildFormFields = () => {
    const dataMap = new Map<string, ChartMetaFormField>();

    PRODUCT_REQUEST_FIELD_CONFIGS.forEach(({ name, label, type }) => {
      dataMap.set(name, chartHelper.createFormField({ name, label, type }));
    });

    return dataMap;
  };

  buildRow = (component: ProductRequestComponentConfig): ProductRequestRow => {
    const { formFieldMap } = this.props;

    return {
      id: v4(),
      active: true,
      componentDesired: buildComponentDesired(component.label, getFieldValue(formFieldMap, component.detailField)),
      numberOfUnits: getFieldValue(formFieldMap, component.unitsField),
      day: getFieldValue(formFieldMap, component.dayField),
      time: getFieldValue(formFieldMap, component.timeField)
    };
  };

  buildRows = (): ProductRequestRow[] => PRODUCT_REQUEST_COMPONENTS.map(this.buildRow).filter((row) => Boolean(row.numberOfUnits || row.day || row.time));

  // Values are kept after save so the entries can be edited via the edit icon on the step, and the
  // saved snapshot becomes the baseline an abandoned edit reverts to.
  handleSaveClick = () => {
    this.props.addProductRequests(this.buildRows());
    this.lastSavedFormFields = cloneFormFieldMap(this.props.formFieldMap);
  };

  render() {
    const chartActionsProps = {
      // Saving with no values is allowed once rows exist so a reset drawer can clear them.
      enableSaveButton: this.buildRows().length > 0 || this.props.hasSavedRows,
      saveButtonText: 'Save',
      onSaveClick: this.handleSaveClick
    };
    const viewProps = {
      chartActionsProps,
      formFieldMap: this.props.formFieldMap,
      formSubmittedCount: this.props.formSubmittedCount,
      isSidebarOpen: this.props.isSidebarOpen,
      onCloseClick: this.handleCloseClick,
      sidebarProps: this.props.sidebarProps
    };
    return <ProductRequestSidebarView {...viewProps} />;
  }
}

export { ProductRequestSidebar as BaseProductRequestSidebar };
export default withChartLogic(ProductRequestSidebar);
